import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc, trpcUntypedClient } from '../lib/api';
import {
  Alert,
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  message,
  Row,
  Col,
  Tag,
  Steps,
  Typography,
} from 'antd';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import {
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { NumericInput, RichTextEditor, PageHeader, PageContainer } from '../components/common';
import EmptyState, { type EmptyStateAction } from '../components/common/EmptyState';
import { useFormAutoBackup } from '../hooks/useFormAutoBackup';
import { FormBackupRestoreModal } from '../components/common/FormBackupRestoreModal';
import { useResponsive } from '../hooks/useResponsive';
import { useAuthStore } from '../stores/authStore';
import { PURCHASE_ORDER_APPROVAL_SETTINGS_URL } from '../constants/purchaseOrderApprovalSettings';
import { calculateIncludedConsumptionTax } from '../utils/purchaseOrderTaxIncludedSummary';

const { Text, Link } = Typography;

interface OrderItem {
  key: string;
  partId: string;
  partName: string;
  partDisplayId: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  unit?: string;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  stockDisplay: string;
  notes?: string;
}

type PurchaseOrderFormValues = {
  supplierId: string;
  orderDate: Dayjs;
  expectedDeliveryDate?: Dayjs;
  managementNumber?: string;
  deliveryLocationNote?: string;
  priceDisplay?: string;
  shippingCost?: number | null;
  notes?: string;
  approvalAssigneeId?: string;
};

type SupplierOption = {
  id: string;
  displayId?: string | null;
  name: string;
};

type PurchaseOrderPart = {
  id: string;
  displayId: string;
  name: string;
  supplierId?: string | null;
  supplier?: SupplierOption | null;
  unitPrice?: string | null;
  unit?: string | null;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
};

type PrefillSupplierGroup = {
  supplierId: string;
  supplierName: string;
  supplierDisplayId?: string | null;
  parts: PurchaseOrderPart[];
};

type PurchaseOrderApprovalSettings = {
  approvalRequired: boolean;
  showCompanySealOnApprovedPdf: boolean;
  approverUserIds: string[];
  defaultApproverUserId: string | null;
};

type PurchaseOrderApproverCandidate = {
  id: string;
  label: string;
  email?: string | null;
};

const defaultApprovalSettings: PurchaseOrderApprovalSettings = {
  approvalRequired: false,
  showCompanySealOnApprovedPdf: true,
  approverUserIds: [],
  defaultApproverUserId: null,
};

const normalizeNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (value === null || value === undefined) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const formatStockDisplay = (part: Pick<PurchaseOrderPart, 'currentStockLevel' | 'optimalStockLevel' | 'unit'>): string => {
  const currentStock = normalizeNumber(part.currentStockLevel);
  const optimalStock = normalizeNumber(part.optimalStockLevel);
  const unit = part.unit || '個';
  return `在庫 ${currentStock.toLocaleString()} / ${optimalStock.toLocaleString()} ${unit}`;
};

const buildOrderItem = (part: PurchaseOrderPart, quantity = 1): OrderItem => {
  const unitPrice = normalizeNumber(part.unitPrice);
  const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  return {
    key: part.id,
    partId: part.id,
    partName: part.name,
    partDisplayId: part.displayId,
    quantity: safeQuantity,
    unitPrice,
    amount: safeQuantity * unitPrice,
    unit: part.unit || '個',
    currentStockLevel: part.currentStockLevel,
    optimalStockLevel: part.optimalStockLevel,
    stockDisplay: formatStockDisplay(part),
  };
};

export function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const [form] = Form.useForm<PurchaseOrderFormValues>();
  const shippingCostWatch = Form.useWatch('shippingCost', form);
  const [messageApi, contextHolder] = message.useMessage();
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedPartId, setSelectedPartId] = useState<string | undefined>();
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [approvalSettings, setApprovalSettings] = useState<PurchaseOrderApprovalSettings>(defaultApprovalSettings);
  const [approverCandidates, setApproverCandidates] = useState<PurchaseOrderApproverCandidate[]>([]);
  const appliedPrefillKeyRef = useRef<string | null>(null);
  const prefillPartIdsParam = searchParams.get('partIds') ?? '';
  const prefillQuantitiesParam = searchParams.get('quantities') ?? '';
  const prefillPartIds = useMemo(
    () => prefillPartIdsParam.split(',').map((id) => id.trim()).filter(Boolean),
    [prefillPartIdsParam]
  );
  const prefillPartIdsKey = prefillPartIds.join(',');
  const prefillQuantityMap = useMemo(() => {
    const quantities = new Map<string, number>();

    prefillQuantitiesParam
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const separatorIndex = entry.lastIndexOf(':');
        if (separatorIndex <= 0) {
          return;
        }

        const partId = entry.slice(0, separatorIndex);
        const quantity = Number(entry.slice(separatorIndex + 1));
        if (partId && Number.isFinite(quantity) && quantity > 0) {
          quantities.set(partId, quantity);
        }
      });

    return quantities;
  }, [prefillQuantitiesParam]);
  const prefillKey = `${prefillPartIdsKey}|${prefillQuantitiesParam}`;

  // フォーム自動バックアップ（新規作成のみ）
  const { hasBackup, backupData, restoreBackup, clearBackup } = useFormAutoBackup({
    formType: 'purchase_order',
    entityId: null,
    form,
    enabled: true,
  });

  // 初回マウント時にバックアップ確認
  useEffect(() => {
    if (hasBackup) {
      setShowRestoreModal(true);
    }
  }, [hasBackup]);

  // 業者一覧取得
  const { data: suppliersData, isLoading: isSuppliersLoading } = trpc.suppliers.list.useQuery({
    page: 1,
    limit: 100,
  });

  // 部品・消耗品一覧取得
  const { data: partsData, isLoading: isPartsLoading } = trpc.parts.list.useQuery({
    page: 1,
    limit: 1000,
  });

  const suppliers = useMemo(
    () => (suppliersData?.items || []) as SupplierOption[],
    [suppliersData?.items]
  );
  const parts = useMemo(
    () => (partsData?.items || []) as PurchaseOrderPart[],
    [partsData?.items]
  );
  const suppliersTotal = suppliersData?.pagination?.total ?? suppliers.length;
  const partsTotal = partsData?.pagination?.total ?? parts.length;
  const missingSuppliers = !isSuppliersLoading && suppliersTotal === 0;
  const missingParts = !isPartsLoading && partsTotal === 0;
  const shouldShowEmptyState = missingSuppliers || missingParts;
  const supplierMap = useMemo(() => {
    const map = new Map<string, SupplierOption>();
    suppliers.forEach((supplier) => {
      map.set(supplier.id, supplier);
    });
    return map;
  }, [suppliers]);
  const prefillParts = useMemo(
    () =>
      prefillPartIds
        .map((partId) => parts.find((part) => part.id === partId))
        .filter((part): part is PurchaseOrderPart => !!part),
    [parts, prefillPartIds]
  );
  const prefillPartsWithoutSupplier = useMemo(
    () => prefillParts.filter((part) => !part.supplierId),
    [prefillParts]
  );
  const prefillSupplierGroups = useMemo<PrefillSupplierGroup[]>(() => {
    const groups = new Map<string, PrefillSupplierGroup>();

    prefillParts.forEach((part) => {
      if (!part.supplierId) {
        return;
      }

      const supplier = supplierMap.get(part.supplierId) ?? part.supplier ?? null;
      const existing = groups.get(part.supplierId);
      if (existing) {
        existing.parts.push(part);
        return;
      }

      groups.set(part.supplierId, {
        supplierId: part.supplierId,
        supplierName: supplier?.name ?? '購入先未登録',
        supplierDisplayId: supplier?.displayId,
        parts: [part],
      });
    });

    return Array.from(groups.values());
  }, [prefillParts, supplierMap]);
  const shouldShowPrefillCandidates = prefillPartIds.length > 0 && prefillParts.length > 0;
  const companyName = user?.companyName || '発注元会社';
  const contactName = user?.name || '担当者';
  const contactEmail = user?.email || '-';
  const companyId = user?.companyId;

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;

    void (async () => {
      try {
        const [nextSettings, nextCandidates] = await Promise.all([
          trpcUntypedClient.query('company.getPurchaseOrderApprovalSettings', { companyId }) as Promise<PurchaseOrderApprovalSettings>,
          trpcUntypedClient.query('company.getPurchaseOrderApproverCandidates', { companyId }) as Promise<PurchaseOrderApproverCandidate[]>,
        ]);
        if (!isMounted) return;
        const normalizedSettings = {
          ...defaultApprovalSettings,
          ...(nextSettings ?? {}),
          approverUserIds: nextSettings?.approverUserIds ?? [],
          defaultApproverUserId: nextSettings?.defaultApproverUserId ?? null,
        };
        setApprovalSettings(normalizedSettings);
        setApproverCandidates(nextCandidates ?? []);
        if (normalizedSettings.approvalRequired && normalizedSettings.defaultApproverUserId) {
          form.setFieldsValue({ approvalAssigneeId: normalizedSettings.defaultApproverUserId });
        }
      } catch (error) {
        const messageText = error instanceof Error ? error.message : '承認設定の取得に失敗しました';
        messageApi.error(messageText);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [companyId, form, messageApi]);

  const applyPrefillGroup = (supplierId: string) => {
    const group = prefillSupplierGroups.find((candidate) => candidate.supplierId === supplierId);
    if (!group) {
      return;
    }

    form.setFieldsValue({ supplierId: group.supplierId });
    setOrderItems(group.parts.map((part) => buildOrderItem(part, prefillQuantityMap.get(part.id))));
    setSelectedPartId(undefined);
    appliedPrefillKeyRef.current = `${prefillKey}:${group.supplierId}`;
  };

  useEffect(() => {
    if (!prefillPartIdsKey || prefillSupplierGroups.length !== 1) {
      return;
    }

    const [group] = prefillSupplierGroups;
    const appliedKey = `${prefillKey}:${group.supplierId}`;
    if (appliedPrefillKeyRef.current === appliedKey) {
      return;
    }

    form.setFieldsValue({ supplierId: group.supplierId });
    setOrderItems(group.parts.map((part) => buildOrderItem(part, prefillQuantityMap.get(part.id))));
    setSelectedPartId(undefined);
    appliedPrefillKeyRef.current = appliedKey;
  }, [form, prefillKey, prefillPartIdsKey, prefillQuantityMap, prefillSupplierGroups]);

  // 発注作成mutation
  const createMutation = trpc.purchaseOrders.create.useMutation({
    onSuccess: (data) => {
      messageApi.success('発注ドラフトを作成しました');
      clearBackup(); // 保存成功時にバックアップをクリア
      navigate(`/app/purchase-orders/${data.order.id}/pdf`);
    },
    onError: (error) => {
      messageApi.error(`発注作成に失敗しました: ${error.message}`);
    },
  });

  // 部品・消耗品追加
  const handleAddItem = () => {
    if (!selectedPartId) {
      messageApi.warning('部品・消耗品を選択してください');
      return;
    }

    const selectedPart = parts.find(p => p.id === selectedPartId);
    if (!selectedPart) return;

    // 既に追加済みの場合はスキップ
    if (orderItems.some(item => item.partId === selectedPartId)) {
      messageApi.warning('この部品・消耗品は既に追加されています');
      return;
    }

    setOrderItems([...orderItems, buildOrderItem(selectedPart)]);
    setSelectedPartId(undefined);
  };

  // 明細削除
  const handleRemoveItem = (key: string) => {
    setOrderItems(orderItems.filter(item => item.key !== key));
  };

  // 数量変更
  const handleQuantityChange = (key: string, quantity: number) => {
    const safeQuantity = Number.isFinite(quantity) ? Math.max(quantity, 1) : 1;
    setOrderItems(
      orderItems.map(item =>
        item.key === key
          ? { ...item, quantity: safeQuantity, amount: safeQuantity * item.unitPrice }
          : item
      )
    );
  };

  // 単価変更
  const handleUnitPriceChange = (key: string, unitPrice: number) => {
    setOrderItems(
      orderItems.map(item =>
        item.key === key
          ? { ...item, unitPrice, amount: item.quantity * unitPrice }
          : item
      )
    );
  };

  const ensureOrderItemsPresent = () => {
    if (orderItems.length === 0) {
      messageApi.warning('少なくとも1つの部品・消耗品を追加してください');
      return false;
    }
    return true;
  };

  // 合計金額計算
  const subtotal = orderItems.reduce((sum, item) => sum + item.amount, 0);
  const shippingCost = normalizeNumber(shippingCostWatch);
  const tax = calculateIncludedConsumptionTax(subtotal);
  const totalAmount = subtotal + shippingCost;

  // 発注送信
  const handleFormSubmit = (values: PurchaseOrderFormValues) => {
    if (!ensureOrderItemsPresent()) {
      return;
    }
    if (approvalSettings.approvalRequired && !values.approvalAssigneeId) {
      messageApi.warning('承認先を選択してください');
      return;
    }

    createMutation.mutate({
      supplierId: values.supplierId,
      orderDate: values.orderDate.toDate(),
      expectedDeliveryDate: values.expectedDeliveryDate?.toDate(),
      items: orderItems.map(item => ({
        partId: item.partId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        notes: item.notes,
      })),
      shippingCost: normalizeNumber(values.shippingCost),
      notes: values.notes,
      approvalAssigneeId: approvalSettings.approvalRequired ? values.approvalAssigneeId : undefined,
    });
  };

  const handleSubmitClick = () => {
    if (!ensureOrderItemsPresent()) {
      return;
    }
    form.submit();
  };

  const handleAdjustQuantity = (record: OrderItem, delta: number) => {
    handleQuantityChange(record.key, record.quantity + delta);
  };

  const renderMobileOrderItems = () => {
    if (orderItems.length === 0) {
      return <Text type="secondary">部品・消耗品を追加してください</Text>;
    }

    return (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {orderItems.map((item) => (
          <div
            key={item.key}
            data-testid={`mobile-order-item-${item.partId}`}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: 12,
              background: '#fff',
            }}
          >
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Space wrap size={6}>
                <Text strong>{item.partName}</Text>
                <Tag>{item.partDisplayId}</Tag>
              </Space>
              <Text type="secondary">{item.stockDisplay}</Text>

              <div>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  発注数量
                </Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Button
                    htmlType="button"
                    icon={<MinusOutlined aria-hidden="true" />}
                    aria-label={`${item.partName}の発注数量を1減らす`}
                    onClick={() => handleAdjustQuantity(item, -1)}
                  />
                  <NumericInput
                    min={1}
                    value={item.quantity}
                    onChange={(value) => {
                      const next = typeof value === 'number' ? value : value ? Number(value) : NaN;
                      handleQuantityChange(item.key, Number.isFinite(next) ? next : 1);
                    }}
                    aria-label={`${item.partName}の発注数量`}
                    style={{ width: '100%' }}
                  />
                  <Button
                    htmlType="button"
                    icon={<PlusOutlined aria-hidden="true" />}
                    aria-label={`${item.partName}の発注数量を1増やす`}
                    onClick={() => handleAdjustQuantity(item, 1)}
                  />
                </Space.Compact>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  単位: {item.unit}
                </Text>
              </div>

              <div>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  単価(税込)
                </Text>
                <NumericInput
                  min={0}
                  value={item.unitPrice}
                  onChange={(value) => {
                    const next = typeof value === 'number' ? value : value ? Number(value) : NaN;
                    handleUnitPriceChange(item.key, Number.isFinite(next) ? next : 0);
                  }}
                  addonBefore="¥"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  aria-label={`${item.partName}の単価(税込)`}
                  style={{ width: '100%' }}
                />
              </div>

              <Row justify="space-between" align="middle">
                <Col>
                      <Text type="secondary">金額(税込)</Text>
                </Col>
                <Col>
                  <Text strong>{`¥${item.amount.toLocaleString()}`}</Text>
                </Col>
              </Row>

              <Button
                block
                danger
                htmlType="button"
                icon={<DeleteOutlined aria-hidden="true" />}
                onClick={() => handleRemoveItem(item.key)}
              >
                明細から削除
              </Button>
            </Space>
          </div>
        ))}
      </Space>
    );
  };

  // テーブルカラム定義
  const itemColumns: ResponsiveColumnType<OrderItem>[] = [
    {
      title: '部品・消耗品ID',
      dataIndex: 'partDisplayId',
      key: 'partDisplayId',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '部品・消耗品名',
      dataIndex: 'partName',
      key: 'partName',
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '在庫',
      dataIndex: 'stockDisplay',
      key: 'stockDisplay',
      width: 140,
      render: (stockDisplay) => <Text type="secondary">{stockDisplay}</Text>,
      mobileRole: 'description',
      mobilePriority: 2,
    },
    {
      title: '数量',
      key: 'quantity',
      width: 150,
      render: (_, record) => (
        <NumericInput
          min={1}
          value={record.quantity}
          onChange={(value) => {
            const next = typeof value === 'number' ? value : value ? Number(value) : NaN;
            handleQuantityChange(record.key, Number.isFinite(next) ? next : 1);
          }}
          aria-label={`${record.partName}の発注数量`}
          addonAfter={record.unit}
        />
      ),
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '単価(税込)',
      key: 'unitPrice',
      width: 150,
      render: (_, record) => (
        <NumericInput
          min={0}
          value={record.unitPrice}
          onChange={(value) => {
            const next = typeof value === 'number' ? value : value ? Number(value) : NaN;
            handleUnitPriceChange(record.key, Number.isFinite(next) ? next : 0);
          }}
          aria-label={`${record.partName}の単価(税込)`}
          addonBefore="¥"
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      ),
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '金額(税込)',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount) => `¥${amount.toLocaleString()}`,
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined aria-hidden="true" />}
          onClick={() => handleRemoveItem(record.key)}
        >
          削除
        </Button>
      ),
      mobileRole: 'hidden',
    },
  ];
  const availableApproverOptions = approverCandidates
    .filter((candidate) => approvalSettings.approverUserIds.includes(candidate.id))
    .map((candidate) => ({
      value: candidate.id,
      label: candidate.label,
    }));
  const isApprovalAssigneeUnavailable =
    approvalSettings.approvalRequired && availableApproverOptions.length === 0;

  if (shouldShowEmptyState) {
    const emptyActions: EmptyStateAction[] = [];
    if (missingSuppliers) {
      emptyActions.push({
        label: '業者を登録する',
        type: 'primary',
        onClick: () => navigate('/app/suppliers'),
      });
    }
    if (missingParts) {
      emptyActions.push({
        label: '部品・消耗品を登録する',
        type: missingSuppliers ? 'default' : 'primary',
        onClick: () => navigate('/app/parts'),
      });
    }

    return (
      <PageContainer>
        {contextHolder}

        <PageHeader
          title="新規発注作成"
          subtitle="業者への部品・消耗品の発注を作成します"
          extra={
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/app/purchase-orders')}
            >
              発注一覧に戻る
            </Button>
          }
        />

        <EmptyState
          icon={<ShoppingCartOutlined />}
          title="発注作成の前に登録が必要です"
          description={
            <>
              {missingSuppliers && <>発注先となる業者が未登録です。<br /></>}
              {missingParts && <>発注対象となる部品・消耗品が未登録です。<br /></>}
              先に登録を済ませてください。
            </>
          }
          actions={emptyActions}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {contextHolder}

      {/* バックアップ復元確認モーダル */}
      <FormBackupRestoreModal
        open={showRestoreModal}
        savedAt={backupData?.savedAt ?? 0}
        onRestore={() => {
          restoreBackup();
          setShowRestoreModal(false);
        }}
        onDiscard={() => {
          clearBackup();
          setShowRestoreModal(false);
        }}
      />

      <PageHeader
        title="発注書ドラフトを編集"
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/purchase-orders')}
          >
            発注一覧に戻る
          </Button>
        }
      />

      <Steps
        size="small"
        current={1}
        items={[
          { title: '品目選択' },
          { title: 'ドラフト編集' },
          { title: 'PDF確認' },
          { title: '出力完了' },
        ]}
      />

      {shouldShowPrefillCandidates && (
        <Card title="業者別ドラフト候補" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {prefillSupplierGroups.length > 0 && (
              <Row gutter={[12, 12]}>
                {prefillSupplierGroups.map((group) => (
                  <Col xs={24} md={12} key={group.supplierId}>
                    <div
                      data-testid={`prefill-supplier-${group.supplierId}`}
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                        padding: 12,
                      }}
                    >
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space wrap>
                          <Text strong>{group.supplierName}</Text>
                          {group.supplierDisplayId && <Text type="secondary">{group.supplierDisplayId}</Text>}
                          <Tag color="blue">{group.parts.length}件</Tag>
                        </Space>
                        <Text type="secondary">
                          {group.parts.map((part) => part.name).join('、')}
                        </Text>
                        <Button
                          block={isMobile}
                          type="primary"
                          onClick={() => applyPrefillGroup(group.supplierId)}
                        >
                          この業者の発注内容を編集する
                        </Button>
                      </Space>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {prefillPartsWithoutSupplier.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`購入先未設定のため発注候補から外れています: ${prefillPartsWithoutSupplier
                  .map((part) => part.name)
                  .join('、')}`}
              />
            )}
          </Space>
        </Card>
      )}

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={14}>
      {/* 発注情報フォーム */}
      <Card title="発注情報" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="業者"
                name="supplierId"
                rules={[{ required: true, message: '業者を選択してください' }]}
              >
                <Select
                  placeholder="業者を選択"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={suppliers.map(s => ({
                    label: `${s.name} (${s.displayId})`,
                    value: s.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="発注日"
                name="orderDate"
                rules={[{ required: true, message: '発注日を選択してください' }]}
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="納品予定日" name="expectedDeliveryDate">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="管理番号" name="managementNumber" initialValue={`PO-${dayjs().format('YYYYMMDD')}`}>
                <Input placeholder="例: PO-MATSUI-0525" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="納品場所補足" name="deliveryLocationNote">
                <Input placeholder="例: 第2工場 保全室" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
            <Form.Item label="送料(税込)" name="shippingCost" initialValue={0}>
                <NumericInput
                  min={0}
                  style={{ width: '100%' }}
                  addonBefore="¥"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="価格表示" name="priceDisplay" initialValue="taxIncluded">
                <Select
                  options={[
                    { label: '税込価格で出力', value: 'taxIncluded' },
                  ]}
                />
              </Form.Item>
            </Col>
            {approvalSettings.approvalRequired && (
              <Col xs={24} md={12}>
                <Form.Item
                  label="承認先"
                  name="approvalAssigneeId"
                  rules={[{ required: true, message: '承認先を選択してください' }]}
                >
                  <Select
                    placeholder="承認先を選択"
                    disabled={availableApproverOptions.length === 0}
                    options={availableApproverOptions}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                label="発注先への備考"
                name="notes"
                getValueFromEvent={(markdown: string) => markdown}
                getValueProps={(value) => ({ value: value || '' })}
              >
                <RichTextEditor
                  placeholder="納品場所の変更、相手先指定事項、自由記述を入力"
                  minHeight={100}
                  showPreviewToggle={false}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 部品・消耗品追加セクション */}
      <Card title="部品・消耗品追加" style={{ marginBottom: 16 }}>
        {isMobile ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Select
              placeholder="部品・消耗品を選択"
              style={{ width: '100%' }}
              showSearch
              value={selectedPartId}
              onChange={setSelectedPartId}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={parts.map(p => ({
                label: `${p.name} (${p.displayId}) - 単価(税込) ¥${parseFloat(p.unitPrice || '0').toLocaleString()} - ${formatStockDisplay(p)}`,
                value: p.id,
              }))}
            />
            <Button
              block
              type="primary"
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={handleAddItem}
            >
              追加
            </Button>
          </Space>
        ) : (
          <Space.Compact style={{ width: '100%' }}>
            <Select
              placeholder="部品・消耗品を選択"
              style={{ width: 'calc(100% - 100px)' }}
              showSearch
              value={selectedPartId}
              onChange={setSelectedPartId}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={parts.map(p => ({
                label: `${p.name} (${p.displayId}) - 単価(税込) ¥${parseFloat(p.unitPrice || '0').toLocaleString()} - ${formatStockDisplay(p)}`,
                value: p.id,
              }))}
            />
            <Button
              type="primary"
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={handleAddItem}
            >
              追加
            </Button>
          </Space.Compact>
        )}
      </Card>

      {/* 発注明細テーブル */}
      <Card title="発注明細" style={{ marginBottom: 16 }}>
        {isMobile ? (
          renderMobileOrderItems()
        ) : (
          <ResponsiveTable<OrderItem>
            columns={itemColumns}
            dataSource={orderItems}
            pagination={false}
            locale={{ emptyText: '部品・消耗品を追加してください' }}
            mobileCardConfig={{
              titleField: 'partName',
              actions: (record) => (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined aria-hidden="true" />}
                  onClick={() => handleRemoveItem(record.key)}
                >
                  削除
                </Button>
              ),
            }}
          />
        )}
      </Card>

        </Col>
        <Col xs={24} lg={10}>
      <Card title="発注元と担当者" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card size="small" title="発注元会社">
            <Space direction="vertical" size={4}>
              <Text strong>{companyName}</Text>
            </Space>
          </Card>
          <Card size="small" title="担当者">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Input value={contactName} readOnly />
              <Text type="secondary">{contactEmail}</Text>
            </Space>
          </Card>
        </Space>
      </Card>

      <Card title="確認事項" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {approvalSettings.approvalRequired && (
            <Row justify="space-between">
              <Col>
                <Space>
                  <CheckCircleOutlined style={{ color: isApprovalAssigneeUnavailable ? '#faad14' : '#389e0d' }} />
                  <Text>承認必須</Text>
                </Space>
              </Col>
              <Col>
                <Tag color={isApprovalAssigneeUnavailable ? 'warning' : 'processing'}>
                  {isApprovalAssigneeUnavailable ? '承認先未設定' : '承認先を指定'}
                </Tag>
              </Col>
            </Row>
          )}
          {isApprovalAssigneeUnavailable && (
            <Alert
              type="warning"
              showIcon
              message={
                <Space direction="vertical" size={4}>
                  <Text>会社設定で承認可能ユーザーとデフォルト承認先を設定してください</Text>
                  <Link href={PURCHASE_ORDER_APPROVAL_SETTINGS_URL}>承認設定はこちら</Link>
                </Space>
              }
            />
          )}
          <Row justify="space-between">
            <Col>
              <Space>
                <CheckCircleOutlined style={{ color: '#389e0d' }} />
                <Text>発注先の連絡先をPDFに表示</Text>
              </Space>
            </Col>
            <Col>
              <Tag color="success">OK</Tag>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col>
              <Space>
                <CheckCircleOutlined style={{ color: '#389e0d' }} />
                <Text>ダウンロード・印刷で発注</Text>
              </Space>
            </Col>
            <Col>
              <Tag color="success">OK</Tag>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* 金額サマリー */}
      <Card title="金額計算">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>小計(税込):</Text>
                <Text strong>¥{subtotal.toLocaleString()}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>内消費税:</Text>
                <Text>¥{tax.toLocaleString()}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>送料(税込):</Text>
                <Text>¥{shippingCost.toLocaleString()}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <Text strong style={{ fontSize: 16 }}>税込合計:</Text>
                <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                  ¥{totalAmount.toLocaleString()}
                </Text>
              </div>
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: 24, textAlign: isMobile ? 'left' : 'right' }}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={isMobile ? { width: '100%' } : undefined}>
            <Button block={isMobile} onClick={() => navigate('/app/purchase-orders')}>
              キャンセル
            </Button>
            <Button
              block={isMobile}
              type="primary"
              icon={<FilePdfOutlined aria-hidden="true" />}
              onClick={handleSubmitClick}
              loading={createMutation.isPending}
              disabled={orderItems.length === 0 || isApprovalAssigneeUnavailable}
            >
              確認して出力へ進む
            </Button>
          </Space>
        </div>
      </Card>
        </Col>
      </Row>
    </PageContainer>
  );
}
